/* Eine Lesart fuer Client, Desktop-Aufnahme und Server (electron/konto-freigabe.mjs). */
import { passtZuFreigabe } from '../../../electron/konto-freigabe.mjs';
export { passtZuFreigabe };

/** One login per cookie box, even when several stage tabs open at once. */
const boxen = new Map<string, Promise<unknown>>();
export async function inPartnerBox<T>(box: string, arbeit: () => Promise<T>): Promise<T> {
  const vorher = boxen.get(box);
  const jetzt = (vorher ? vorher.catch(() => undefined) : Promise.resolve()).then(arbeit);
  boxen.set(box, jetzt);
  try { return await jetzt; }
  finally { if (boxen.get(box) === jetzt) boxen.delete(box); }
}

type Antwort = { ok: boolean; status: number; data: any; error?: string };
type Optionen = {
  email: string;
  label: string;
  ziel: string;
  aktualisieren?: boolean;
  call: (path: string, init?: { method?: string; body?: unknown }) => Promise<Antwort>;
  ticket: () => Promise<string>;
  navigate: (url: string) => Promise<void>;
  status: (text: string) => void;
  aktiv: () => boolean;
  /** Wartezeit vor dem n-ten erneuten Blick (0-basiert). Vorgabe: wachsender Abstand, siehe `wartezeitMs`. */
  warten?: (n: number) => Promise<void>;
  /** Adresse des Dokuments, das die Webview WIRKLICH zeigt (location.href), '' wenn nicht lesbar. */
  herkunft?: () => Promise<string>;
};

/** Wachsender Abstand statt festem 500-ms-Takt: 0,5 s, 1 s, 2 s, danach 4 s. */
export function wartezeitMs(n: number): number {
  return Math.min(4000, 500 * 2 ** Math.max(0, n));
}

/**
 * Wo steht das Fenster? Nach einem Navigationsfehler (DNS, Netz, Zertifikat) zeigt die
 * Webview chrome-error://chromewebdata/, waehrend getURL() weiter die Zieladresse meldet
 * (siehe BrowserPanel, Ladefehler). Ein fetch('/api/auth/me') laeuft dort gegen
 * chrome-error://chromewebdata/api/auth/me und scheitert immer — gemessen 01.11.2026
 * 13:20:38–13:21:15Z an #guide-p-browser-0-1na9lzh: 41 Abfragen im 500-ms-Takt.
 */
export function herkunftLage(href: string, ziel: string): 'passt' | 'fehlerseite' | 'leer' | 'fremd' {
  if (!href || href === 'about:blank') return 'leer';
  if (href.startsWith('chrome-error:')) return 'fehlerseite';
  let origin: string;
  try { origin = new URL(href).origin; } catch { return 'leer'; } // silent-ok: unlesbare Adresse = noch kein Dokument, der Aufrufer wartet sichtbar
  return origin === new URL(ziel).origin ? 'passt' : 'fremd';
}

/** Steht das Fenster nicht auf der Partnerplattform, wird nicht gefragt; neu angestossen wird erst durch eine erfolgreiche Navigation. */
export class PartnerfensterNichtGeladen extends Error {}

/** Verify in the actual webview after every transition. Navigation events may arrive
 * while loadURL is pending; progress must not depend on React observing that event. */
export async function meldePartnerKontoAn(o: Optionen): Promise<boolean> {
  let aktualisiert = !o.aktualisieren;
  let tickets = 0;
  let uebernahmen = 0;
  const warten = o.warten ?? ((n: number) => new Promise<void>(r => setTimeout(r, wartezeitMs(n))));
  let gewartet = 0;
  for (let versuch = 0; versuch < 16 && o.aktiv(); versuch++) {
    if (o.herkunft) {
      const href = await o.herkunft();
      if (!o.aktiv()) return false;
      const lage = herkunftLage(href, o.ziel);
      const erwartet = new URL(o.ziel).origin;
      if (lage === 'fehlerseite') {
        throw new PartnerfensterNichtGeladen(`Das Partnerfenster konnte ${erwartet} nicht laden (Navigationsfehler, siehe Meldung im Fenster). Anmeldung pausiert, bis die Seite wieder lädt.`);
      }
      if (lage === 'fremd') {
        throw new PartnerfensterNichtGeladen(`Das Partnerfenster zeigt ${new URL(href).origin} statt ${erwartet}. Anmeldung pausiert, bis die Partnerplattform geladen ist.`);
      }
      if (lage === 'leer') {
        o.status('Warte auf das Partnerfenster …');
        await warten(gewartet++);
        continue;
      }
    }
    const me = await o.call('/api/auth/me');
    if (!o.aktiv()) return false;
    if (me.ok && passtZuFreigabe(me.data?.user ?? {}, o.email)) {
      if (!aktualisiert) {
        aktualisiert = true;
        await o.navigate(o.ziel);
        continue;
      }
      o.status(`Angemeldet als ${o.label}.`);
      return true;
    }
    if (!me.ok && me.status <= 0) {
      o.status('Warte auf das Partnerfenster …');
      await warten(gewartet++);
      continue;
    }
    if (!me.ok && me.status !== 401) throw new Error(`Anmeldestatus nicht lesbar — HTTP ${me.status}`);
    if (me.ok && me.data?.user?.role === 'admin' && !me.data?.impersonatedBy) {
      if (++uebernahmen > 2) throw new Error('Das Partnerfenster bestätigt das gewählte Konto nicht.');
      o.status(`Melde als ${o.label} an …`);
      const dir = await o.call('/api/auth/users');
      if (!o.aktiv()) return false;
      if (!dir.ok) throw new Error(`Nutzerverzeichnis nicht lesbar — HTTP ${dir.status}`);
      const treffer = (dir.data?.users ?? []).find((u: { id: string; email?: string }) => passtZuFreigabe(u, o.email));
      if (!treffer) throw new Error(`Kein CUI-Nutzer mit ${o.email} gefunden — nicht angemeldet.`);
      const imp = await o.call('/api/auth/impersonate', { method: 'POST', body: { userId: treffer.id } });
      if (!o.aktiv()) return false;
      if (!imp.ok) throw new Error(`Konto-Anmeldung fehlgeschlagen — HTTP ${imp.status}: ${imp.data?.error || imp.error || ''}`);
      aktualisiert = true;
      await o.navigate(o.ziel);
    } else {
      if (++tickets > 2) throw new Error('Die Anmeldung wurde im Partnerfenster nicht übernommen.');
      o.status('Bereite die Konto-Anmeldung vor …');
      const ticket = await o.ticket();
      if (!o.aktiv()) return false;
      await o.navigate(ticket);
    }
  }
  if (!o.aktiv()) return false;
  throw new Error('Das Partnerfenster antwortet nicht. Bitte erneut versuchen.');
}
